import React from 'react';
import { IconNameEnum } from '@app/common';
import { darken } from 'polished';
import { DefaultTheme, useTheme } from 'styled-components';
import { IconPortfolioSizeType, IconPortfolioVariantType } from '../../../../theme/type/icon-portfolio-type';
import { getIcon } from '../../../../utils/get-icon';
import { BreakpointSizeType } from '../../../../theme/type/breakpoints-type';
import { breakpointPrefix, cn } from '../../../../utils/tailwind';
import { createStableClassToken } from '../../../../utils/stable-class-token';

type IconPortfolioResponsiveSize = Partial<Record<BreakpointSizeType, IconPortfolioSizeType>>;

const iconPortfolioSizeClass: Record<IconPortfolioSizeType, string> = {
    xsmall: 'h-[3rem] w-[3rem] min-w-[3rem]',
    vsmall: 'h-[3.2rem] w-[3.2rem] min-w-[3.2rem]',
    small: 'h-[3.6rem] w-[3.6rem] min-w-[3.6rem]',
    medium: 'h-[4rem] w-[4rem] min-w-[4rem]',
    large: 'h-[4.8rem] w-[4.8rem] min-w-[4.8rem]',
    vlarge: 'h-[5.2rem] w-[5.2rem] min-w-[5.2rem]',
    xlarge: 'h-[6.2rem] w-[6.2rem] min-w-[6.2rem]',
};

const getResponsiveSizeClasses = (size: IconPortfolioResponsiveSize) => {
    const entries = Object.entries(size) as [BreakpointSizeType, IconPortfolioSizeType][];

    return entries.map(([breakpoint, value], index) => {
        const className = iconPortfolioSizeClass[value];

        if (index === 0) {
            return className;
        }

        return className
            .split(' ')
            .filter(Boolean)
            .map((token) => `${breakpointPrefix[breakpoint]}${token}`)
            .join(' ');
    });
};

const getIconSizeCss = (
    className: string,
    size: IconPortfolioSizeType | IconPortfolioResponsiveSize,
    theme: DefaultTheme,
) => {
    if (typeof size === 'string') {
        return `
            .${className} .app-icon,
            .${className} .app-icon svg {
                width: ${theme.iconPortfolio.size[size].icon} !important;
                min-width: ${theme.iconPortfolio.size[size].icon} !important;
                height: ${theme.iconPortfolio.size[size].icon} !important;
            }
        `;
    }

    return (Object.entries(size) as [BreakpointSizeType, IconPortfolioSizeType][])
        .map(([breakpoint, value], index) => {
            const rules = `
                .${className} .app-icon,
                .${className} .app-icon svg {
                    width: ${theme.iconPortfolio.size[value].icon} !important;
                    min-width: ${theme.iconPortfolio.size[value].icon} !important;
                    height: ${theme.iconPortfolio.size[value].icon} !important;
                }
            `;

            if (index === 0) {
                return `
                    @media ${theme.breakpoints[breakpoint].max} { ${rules} }
                    @media ${theme.breakpoints[breakpoint].min} { ${rules} }
                `;
            }

            return `@media ${theme.breakpoints[breakpoint].min} { ${rules} }`;
        })
        .join('\n');
};

export type AssetIconType = {
    type: IconNameEnum;
    variant: IconPortfolioVariantType;
    variation?: 'filled' | 'outlined';
    size?: IconPortfolioSizeType | IconPortfolioResponsiveSize;
    dark?: boolean;
};

export const AssetIcon: React.FC<AssetIconType> = ({
    type,
    variant,
    size = 'medium',
    dark = false,
    variation = 'filled',
}) => {
    const theme = useTheme();
    const portfolioIcon = getIcon(type);
    const iconSizeClassToken = createStableClassToken('asset-icon-size', [size]);

    let sizeClassName = iconPortfolioSizeClass.medium;

    if (typeof size === 'string') {
        sizeClassName = iconPortfolioSizeClass[size];
    } else if (size) {
        sizeClassName = getResponsiveSizeClasses(size).join(' ');
    }

    const baseVariant = theme.iconPortfolio.variant[variant].base;

    let backgroundColor: string | undefined;
    const { color: baseColor, border: baseBorder, background: baseBackground } = baseVariant;
    let color = baseColor;
    let borderWidth = '0.1rem';

    if (variation === 'filled') {
        backgroundColor = dark ? darken(0.1, baseBackground) : baseBackground;
    } else {
        borderWidth = '0.2rem';
        color = baseBorder;
    }

    return (
        <>
            <style>{getIconSizeCss(iconSizeClassToken, size, theme)}</style>
            <i
                className={cn(
                    iconSizeClassToken,
                    'relative inline-flex items-center justify-center rounded-[30rem] transition-none',
                    '[&_img]:!h-auto [&_svg]:m-0 [&_svg]:inline-flex [&_svg]:fill-transparent [&_svg]:stroke-0 [&_svg]:transition-none',
                    sizeClassName,
                )}
                style={
                    {
                        color,
                        borderColor: baseBorder,
                        borderStyle: 'solid',
                        borderWidth,
                        backgroundColor,
                    } as React.CSSProperties
                }
            >
                {portfolioIcon}
            </i>
        </>
    );
};
